import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import { FunctionComponent, memo, MouseEvent, useCallback } from "react";
import { UnstyledButton } from "../../button";
import { Group } from "../../group";
import { Icon } from "../../icon";
import { Text } from "../../text";
import type { IProps } from "./grouped-list-group-header.types";
import styles from "./grouped-list-group-header.module.scss";

/**
 * A group's header in a `GroupedList`: its label and item count, with a chevron that collapses or expands the group.
 * It carries its own styles, so a caller passes only what it shows and the toggle.
 * @param props.id          The header's element id, which labels the group's listbox.
 * @param props.listboxId   The group's listbox element id, which the header controls.
 * @param props.groupId     The group the header toggles.
 * @param props.label       The group's label.
 * @param props.count       How many items the group lists.
 * @param props.isCollapsed Whether the group is collapsed.
 * @param props.isFocusable Whether the header takes focus (a Tab stop) or keeps it where it is.
 * @param props.onToggle    Collapse or expand the group.
 * @returns The header.
 */
const GroupedListGroupHeader: FunctionComponent<IProps> = ({
    id,
    listboxId,
    groupId,
    label,
    count,
    isCollapsed,
    isFocusable = false,
    onToggle,
}) => {
    const toggle = useCallback(() => onToggle(groupId), [groupId, onToggle]);

    // A press without focus keeps it in the input whose typing drives the list, such as an editor.
    const keepFocus = useCallback((event: MouseEvent) => event.preventDefault(), []);

    return (
        <UnstyledButton
            id={id}
            className={styles.header}
            aria-expanded={!isCollapsed}
            aria-controls={listboxId}
            tabIndex={isFocusable ? undefined : -1}
            onMouseDown={isFocusable ? undefined : keepFocus}
            onClick={toggle}
            w="100%"
            px="md"
            py="sm"
            bdrs="md"
        >
            <Group justify="space-between" wrap="nowrap" gap="sm" c="dimmed">
                <Text size="xs" variant="medium" dimmed>
                    {`${label} (${count.toLocaleString()})`}
                </Text>
                <Icon icon={isCollapsed ? faChevronDown : faChevronUp} />
            </Group>
        </UnstyledButton>
    );
};

export default memo(GroupedListGroupHeader);
